import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Save, Plus, Trash2, Settings2, X, Calculator, AlertCircle, History,
} from "lucide-react";

const groupLabel = (v) => (v && v.includes("|") ? v.split("|").join(" · ") : v || "");

export default function LibroCalificaciones({ profile }) {
  const grades = useMemo(() => (profile?.all_grades || []).map((g) => g.grade), [profile]);
  const [grade, setGrade] = useState("");
  const [term, setTerm] = useState("t1");
  const [terms, setTerms] = useState(["t1", "t2", "t3"]);
  const [termLabels, setTermLabels] = useState({ t1: "Trim. 1", t2: "Trim. 2", t3: "Trim. 3" });
  const [pass, setPass] = useState(7);
  const [subject, setSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [commonCriteria, setCommonCriteria] = useState([]);
  const [addingSubj, setAddingSubj] = useState(false);
  const [newSubj, setNewSubj] = useState("");
  const [students, setStudents] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [mode, setMode] = useState("points");
  const [showHist, setShowHist] = useState(false);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCriteria, setShowCriteria] = useState(false);

  useEffect(() => { if (!grade && grades.length) setGrade(grades[0]); /* eslint-disable-next-line */ }, [grades]);

  const load = async () => {
    if (!grade) return;
    setLoading(true);
    try {
      const { data } = await api.get("/docente/gradebook", { params: { grade, term, subject: subject || undefined } });
      setStudents(data.students || []);
      setCriteria(data.criteria || []);
      setMode(data.mode || "points");
      if (!subject) setSubject(data.subject || "");
      setSubjects(data.subjects || []);
      setCommonCriteria(data.common_criteria || []);
      setTerms(data.terms || ["t1", "t2", "t3"]);
      setTermLabels(data.term_labels || termLabels);
      setPass(data.pass_grade ?? 7);
      const init = {};
      (data.students || []).forEach((s) => { init[s.id] = data.scores?.[s.id] || {}; });
      setScores(init);
    } catch { toast.error("No se pudieron cargar las calificaciones"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [grade, term, subject]);

  const addSubject = async () => {
    const name = newSubj.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/docente/subjects", { subject: name });
      setSubjects(data.subjects || []);
      setNewSubj(""); setAddingSubj(false);
      setSubject(name);
    } catch { toast.error("No se pudo agregar la materia"); }
  };

  const totalWeight = useMemo(() => criteria.reduce((a, c) => a + (Number(c.weight) || 0), 0), [criteria]);

  const setScore = (sid, cid, v) => setScores((r) => ({ ...r, [sid]: { ...(r[sid] || {}), [cid]: v === "" ? null : v } }));
  const weightedAvg = (sid) => {
    const sc = scores[sid] || {};
    let num = 0, den = 0;
    criteria.forEach((c) => {
      const v = sc[c.id];
      if (v === null || v === undefined || v === "") return;
      const w = Number(c.weight) || 0;
      num += mode === "points" ? Number(v) : Number(v) * w; den += w;
    });
    if (den <= 0) return null;
    return Math.round((num / den) * (mode === "points" ? 10 : 1) * 10) / 10;
  };
  // Boleta: a partir de .6 sube al siguiente entero (7.6 → 8, 7.5 → 7).
  const finalGrade = (a) => (a === null ? null : Math.min(10, Math.floor(a) + ((Math.round((a - Math.floor(a)) * 100) / 100) >= 0.6 ? 1 : 0)));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/docente/gradebook/scores", { grade, term, subject, scores, mode });
      toast.success("Calificaciones guardadas · la boleta se actualizó");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const scoreColor = (v) => (v === null || v === undefined || v === "") ? "text-nardo-400" : (Number(v) < pass ? "text-red-600" : "text-emerald-600");

  return (
    <div className="space-y-5" data-testid="docente-calificaciones">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="cal-grade" className="doc-input min-w-[8rem]">
              {grades.length === 0 && <option value="">Sin grupos</option>}
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Trimestre</label>
            <select value={term} onChange={(e) => setTerm(e.target.value)} data-testid="cal-term" className="doc-input min-w-[7rem]">
              {terms.map((t) => <option key={t} value={t}>{termLabels[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Materia</label>
            {addingSubj ? (
              <div className="flex items-center gap-1">
                <input autoFocus value={newSubj} onChange={(e) => setNewSubj(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addSubject(); if (e.key === "Escape") { setAddingSubj(false); setNewSubj(""); } }}
                  data-testid="cal-subject-new" placeholder="Nueva materia" className="doc-input min-w-[9rem]" />
                <button onClick={addSubject} data-testid="cal-subject-add-save" className="text-navy p-2 hover:bg-navy/10 rounded-lg"><Save className="w-4 h-4" /></button>
                <button onClick={() => { setAddingSubj(false); setNewSubj(""); }} className="text-nardo-500 p-2 hover:bg-navy/10 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="cal-subject" className="doc-input min-w-[9rem]">
                  {subjects.length === 0 && subject && <option value={subject}>{subject}</option>}
                  {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => setAddingSubj(true)} data-testid="cal-subject-add" title="Agregar materia" className="text-navy p-2 hover:bg-navy/10 rounded-lg border border-navy/20"><Plus className="w-4 h-4" /></button>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[8rem] flex justify-end gap-2">
            <button onClick={() => setShowCriteria(true)} data-testid="cal-criteria-btn"
              className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold transition-colors">
              <Settings2 className="w-4 h-4" /> Criterios
            </button>
            <button onClick={() => setShowHist(true)} disabled={!grade} data-testid="cal-history"
              className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold transition-colors">
              <History className="w-4 h-4" /> Historial
            </button>
            <button onClick={save} disabled={saving || !grade || students.length === 0} data-testid="cal-save"
              className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {criteria.map((c) => (
            <span key={c.id} className="text-[10px] px-2.5 py-1 rounded-full bg-navy/10 text-navy font-medium">{c.label} · {c.weight}%</span>
          ))}
          {totalWeight !== 100 && (
            <span className="text-[10px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-medium inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Los pesos suman {totalWeight}% (ideal 100%)</span>
          )}
        </div>
        <p className="text-[11px] text-nardo-500 mt-2">{mode === "points" ? "Captura los puntos de cada criterio (máximo = su %). Promedio = suma de puntos ÷ 10." : "Captura cada criterio de 0 a 10; se pondera por su %."} El promedio se redondea: a partir de .6 sube al siguiente entero (8.6 → 9) · Aprobatoria {pass}.</p>
        <div className="mt-2 inline-flex rounded-lg border border-nardo-200 overflow-hidden text-[11px]" data-testid="cal-mode">
          <button onClick={() => setMode("points")} data-testid="cal-mode-points" className={`px-3 py-1.5 font-semibold ${mode === "points" ? "bg-navy text-white" : "text-navy"}`}>Por puntos</button>
          <button onClick={() => setMode("grade")} data-testid="cal-mode-grade" className={`px-3 py-1.5 font-semibold ${mode === "grade" ? "bg-navy text-white" : "text-navy"}`}>Calificación 0–10</button>
        </div>
      </div>

      <div className="doc-glass rounded-2xl border border-white/50 shadow-lg overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center text-nardo-500 text-sm">No hay alumnos en este grupo.</div>
        ) : criteria.length === 0 ? (
          <div className="py-16 text-center text-nardo-500 text-sm">Define primero tus criterios de evaluación.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy/5 text-navy text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 sticky left-0 bg-[#eef1f6]">Alumno</th>
                  {criteria.map((c) => (
                    <th key={c.id} className="px-2 py-2.5 w-24 whitespace-nowrap">{c.label}<div className="normal-case text-[9px] text-nardo-500 font-normal">{c.weight}%</div></th>
                  ))}
                  <th className="px-2 py-2.5 w-24 bg-navy/10">Promedio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/40">
                {students.map((s, i) => {
                  const a = weightedAvg(s.id);
                  return (
                    <tr key={s.id} data-testid={`cal-row-${s.id}`}>
                      <td className="px-4 py-2 text-navy font-medium sticky left-0 bg-white/70 whitespace-nowrap">{i + 1}. {s.full_name}</td>
                      {criteria.map((c) => (
                        <td key={c.id} className="px-2 py-2 text-center">
                          <input type="number" min="0" max={mode === "points" ? c.weight : 10} step="0.1" value={scores[s.id]?.[c.id] ?? ""} onChange={(e) => setScore(s.id, c.id, e.target.value)} data-testid={`cal-score-${s.id}-${c.id}`}
                            className={`w-16 text-center border border-nardo-200 rounded-md py-1.5 font-semibold outline-none focus:border-navy ${scoreColor(scores[s.id]?.[c.id])}`} />
                        </td>
                      ))}
                      <td className={`px-2 py-2 text-center font-extrabold text-base bg-navy/10 ${a === null ? "text-nardo-300" : (finalGrade(a) < pass ? "text-red-600" : "text-emerald-600")}`} data-testid={`cal-final-${s.id}`}>{a === null ? "—" : finalGrade(a)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showHist && <HistoryModal grade={grade} term={term} subject={subject} onClose={() => setShowHist(false)} />}
      {showCriteria && (
        <CriteriaModal grade={grade} term={term} termLabel={termLabels[term]} subject={subject} common={commonCriteria} initial={criteria}
          onClose={() => setShowCriteria(false)} onSaved={(c) => { setCriteria(c); setShowCriteria(false); load(); }} />
      )}
    </div>
  );
}

function HistoryModal({ grade, term, subject, onClose }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/docente/gradebook/history", { params: { grade, term, subject } })
      .then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, [grade, term, subject]);
  const shown = (items || []).filter((h) => !q || `${h.student_name} ${h.criterion}`.toLowerCase().includes(q.toLowerCase()));
  const v = (x) => (x === null || x === undefined ? "vacío" : x);
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="cal-history-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-nardo-100 flex items-center gap-3">
          <History className="w-5 h-5 text-navy" />
          <div className="font-semibold text-navy">Historial de cambios</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar alumno o criterio…" data-testid="cal-history-search" className="ml-auto w-48 border border-nardo-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-navy" />
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nardo-50" data-testid="cal-history-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-2">
          {items === null ? <div className="py-10 text-center text-sm text-nardo-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cargando…</div>
            : shown.length === 0 ? <div className="py-10 text-center text-sm text-nardo-500">Sin cambios registrados todavía.</div>
            : shown.map((h) => (
              <div key={h.id} className="flex items-center gap-3 px-3 py-2 border-b border-nardo-50 text-sm" data-testid={`cal-history-${h.id}`}>
                <div className="min-w-0 flex-1"><div className="text-navy font-medium truncate">{h.student_name || "Alumno"}</div>
                  <div className="text-[11px] text-nardo-500">{h.criterion} · {h.by_name || "Docente"} · {new Date(h.at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div></div>
                <div className="text-xs font-mono whitespace-nowrap"><span className="text-red-500 line-through">{v(h.old)}</span> → <span className="text-emerald-600 font-bold">{v(h.new)}</span></div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function CriteriaModal({ grade, term, termLabel, subject, common, initial, onClose, onSaved }) {
  const [rows, setRows] = useState(initial.length ? initial.map((c) => ({ ...c })) : [{ id: "c1", label: "Tareas", weight: 30 }]);
  const [saving, setSaving] = useState(false);
  const total = rows.reduce((a, c) => a + (Number(c.weight) || 0), 0);

  const setRow = (idx, key, val) => setRows((r) => r.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  const add = () => setRows((r) => [...r, { id: `c_${Math.random().toString(36).slice(2, 8)}`, label: "", weight: 0 }]);
  const remove = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const addCommon = (label) => setRows((r) => (r.some((c) => (c.label || "").trim().toLowerCase() === label.toLowerCase())
    ? r : [...r.filter((c) => (c.label || "").trim()), { id: `c_${Math.random().toString(36).slice(2, 8)}`, label, weight: 0 }]));

  const save = async () => {
    const clean = rows.filter((c) => (c.label || "").trim());
    if (!clean.length) { toast.error("Agrega al menos un criterio con nombre"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/docente/gradebook/criteria", { grade, term, subject, criteria: clean });
      toast.success("Criterios guardados");
      onSaved(data.criteria || clean);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="cal-criteria-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy">Criterios · {subject || ""} · {termLabel}</div>
          <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] text-nardo-500">Define cómo se pondera la calificación de <b>{subject || "esta materia"}</b> en este trimestre. Los pesos deberían sumar 100%.</p>
          {(common || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-400 mb-1.5">Sugerencias (toca para agregar)</div>
              <div className="flex flex-wrap gap-1.5">
                {common.map((label) => (
                  <button key={label} type="button" onClick={() => addCommon(label)} data-testid={`crit-common-${label}`}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-navy/5 hover:bg-navy hover:text-white text-navy border border-navy/15 transition-colors inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {rows.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`crit-row-${idx}`}>
              <input value={c.label} onChange={(e) => setRow(idx, "label", e.target.value)} data-testid={`crit-label-${idx}`}
                className="doc-input flex-1" placeholder="Ej. Examen" />
              <div className="relative w-24">
                <input type="number" min="0" max="100" value={c.weight} onChange={(e) => setRow(idx, "weight", e.target.value)} data-testid={`crit-weight-${idx}`}
                  className="doc-input w-full pr-7 text-center" />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-nardo-400">%</span>
              </div>
              <button onClick={() => remove(idx)} data-testid={`crit-del-${idx}`} className="text-red-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={add} data-testid="crit-add" className="text-xs inline-flex items-center gap-1.5 text-navy font-semibold"><Plus className="w-4 h-4" /> Agregar criterio</button>
          <div className={`text-xs font-semibold text-right ${total === 100 ? "text-emerald-600" : "text-amber-600"}`}>Total: {total}%</div>
          <button onClick={save} disabled={saving} data-testid="crit-save"
            className="w-full text-sm inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white py-2.5 rounded-lg font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar criterios
          </button>
        </div>
      </div>
    </div>
  );
}
